/**
 * FILE: packages\supabase\src\services\websiteProductCatalog.ts
 * PURPOSE: Supabase service layer — database access and business logic.
 * AGENT_CONTEXT: All DB access goes through service layer. Call logActivity() on writes.
 * INVARIANTS: Soft-delete filter, phone PK protection, activity logging.
 * LAST_REVIEWED: 2026-05-07
 */


import type { Database } from '../types'
import type { DbClient } from "../dbClient";
import { defaultDbClient } from "../dbClient";

type CatalogRow = Database['public']['Tables']['website_product_catalog']['Row']

export interface WebsiteCatalogStats {
  activeCount: number
  inactiveCount: number
  lastSyncedAt: string | null
  byFamily: Record<string, number>
  unmappedInactive: number
}

export interface WebsiteCatalogListFilters {
  isActive?: boolean
  productFamily?: string
  search?: string
  limit?: number
  offset?: number
}

export async function listWebsiteProductCatalog(
  filters: WebsiteCatalogListFilters = {}, client: DbClient = defaultDbClient
): Promise<{ data: CatalogRow[]; count: number }> {
  const limit = filters.limit ?? 100
  const offset = filters.offset ?? 0

  let query = client
    .from('website_product_catalog')
    .select('*', { count: 'exact' })
    .order('synced_at', { ascending: false })
    .range(offset, offset + limit - 1)

  if (filters.isActive != null) query = query.eq('is_active', filters.isActive)
  if (filters.productFamily) query = query.eq('product_family', filters.productFamily)

  if (filters.search?.trim()) {
    const term = filters.search.trim()
    query = query.or(
      `sanity_id.ilike.%${term}%,display_name.ilike.%${term}%,course.ilike.%${term}%`
    )
  }

  const { data, error, count } = await query
  if (error) throw error

  return { data: data ?? [], count: count ?? 0 }
}

export async function getWebsiteCatalogStats(client: DbClient = defaultDbClient): Promise<WebsiteCatalogStats> {
  const { count: activeCount, error: activeErr } = await client
    .from('website_product_catalog')
    .select('*', { count: 'exact', head: true })
    .eq('is_active', true)

  if (activeErr) throw activeErr

  const { count: inactiveCount, error: inactiveErr } = await client
    .from('website_product_catalog')
    .select('*', { count: 'exact', head: true })
    .eq('is_active', false)

  if (inactiveErr) throw inactiveErr

  const { data: latestRow, error: latestErr } = await client
    .from('website_product_catalog')
    .select('synced_at')
    .order('synced_at', { ascending: false })
    .limit(1)
    .maybeSingle()

  if (latestErr) throw latestErr

  const { data: familyRows, error: familyErr } = await client
    .from('website_product_catalog')
    .select('product_family')
    .eq('is_active', true)

  if (familyErr) throw familyErr

  const byFamily: Record<string, number> = {}
  for (const row of familyRows ?? []) {
    const key = row.product_family ?? 'Unknown'
    byFamily[key] = (byFamily[key] ?? 0) + 1
  }

  const { count: unmappedInactive, error: missErr } = await client
    .from('website_product_catalog')
    .select('*', { count: 'exact', head: true })
    .eq('is_active', false)
    .gte('miss_count', 2)

  if (missErr) throw missErr

  return {
    activeCount: activeCount ?? 0,
    inactiveCount: inactiveCount ?? 0,
    lastSyncedAt: latestRow?.synced_at ?? null,
    byFamily,
    unmappedInactive: unmappedInactive ?? 0,
  }
}

export async function getWebsiteCatalogBySanityId(sanityId: string, client: DbClient = defaultDbClient): Promise<CatalogRow | null> {
  const { data, error } = await client
    .from('website_product_catalog')
    .select('*')
    .eq('sanity_id', sanityId)
    .maybeSingle()

  if (error) throw error
  return data
}
